import { motion, AnimatePresence } from "framer-motion";
import { useState, useEffect, useRef } from "react";
import { ArrowRight, Play, CheckCircle, Star, Phone, Wifi, Battery, Signal } from "lucide-react";

const TRIAL_URL = "https://6vmun24i.forms.app/plumbing-lead-qualification-form";
const DEMO_URL = "https://cal.com/noah-velocitylabs";

const chatMessages = [
{ type: "system", label: "Missed Call Detected", text: "(512) 555-0147", delay: 800 },
{ type: "ai", text: "Thanks for calling Downtown Plumbing, how can I help you today?", delay: 2200 },
{ type: "user", text: "Hi, I have a burst pipe and need a plumber ASAP.", delay: 4200 },
{ type: "ai", text: "I'm sorry to hear that. We have an emergency slot open today at 2:00 PM, would that work?", delay: 6400 },
{ type: "user", text: "Yes, that sounds great!", delay: 8400 },
{ type: "ai", text: "Great. I have booked you for 2:00 PM. See you soon!", delay: 10200 },
{ type: "booked", label: "Job Booked", text: "Emergency plumbing — Today at 2:00 PM", delay: 11800 }];


function ChatBubble({ msg, i }) {
  if (msg.type === "system") {
    return (
      <div className="flex gap-2.5">
        <div className="w-7 h-7 rounded-full bg-red-500/15 flex items-center justify-center flex-shrink-0 mt-0.5">
          <Phone className="w-3 h-3 text-red-400" />
        </div>
        <div className="bg-[#1A1A1A] border border-white/[0.06] rounded-lg p-3">
          <p className="text-[10px] text-red-400 font-medium mb-0.5" style={{ fontFamily: "Manrope, sans-serif" }}>{msg.label}</p>
          <p className="text-xs text-white font-medium" style={{ fontFamily: "Manrope, sans-serif" }}>{msg.text}</p>
        </div>
      </div>);

  }
  if (msg.type === "ai") {
    return (
      <div className="flex gap-2.5">
        <div className="w-7 h-7 rounded-full bg-[#5B8DEF]/20 flex items-center justify-center flex-shrink-0 mt-0.5">
          <div className="w-2.5 h-2.5 rounded-full bg-[#5B8DEF]"></div>
        </div>
        <div className="bg-[#1A1A1A] border border-white/[0.06] rounded-lg p-3 max-w-[75%]">
          <p className="text-xs text-[#A3A3A3] leading-relaxed" style={{ fontFamily: "Manrope, sans-serif" }}>{msg.text}</p>
        </div>
      </div>);

  }
  if (msg.type === "user") {
    return (
      <div className="flex justify-end">
        <div className="bg-[#5B8DEF]/15 border border-[#5B8DEF]/20 rounded-lg p-3 max-w-[75%]">
          <p className="text-xs text-white leading-relaxed" style={{ fontFamily: "Manrope, sans-serif" }}>{msg.text}</p>
        </div>
      </div>);

  }
  if (msg.type === "booked") {
    return (
      <div className="flex gap-2.5">
        <div className="w-7 h-7 rounded-full bg-emerald-500/20 flex items-center justify-center flex-shrink-0 mt-0.5">
          <CheckCircle className="w-3.5 h-3.5 text-emerald-400" />
        </div>
        <div className="bg-[#1A1A1A] border border-white/[0.06] rounded-lg p-3">
          <p className="text-[10px] text-emerald-400 font-medium mb-0.5" style={{ fontFamily: "Manrope, sans-serif" }}>{msg.label}</p>
          <p className="text-xs text-[#A3A3A3]" style={{ fontFamily: "Manrope, sans-serif" }}>{msg.text}</p>
        </div>
      </div>);

  }
  return null;
}

function PhoneMockup() {
  const [visibleMessages, setVisibleMessages] = useState([]);
  const scrollRef = useRef(null);

  useEffect(() => {
    const timers = chatMessages.map((msg, i) =>
    setTimeout(() => {
      setVisibleMessages((prev) => [...prev, i]);
    }, msg.delay)
    );
    return () => timers.forEach(clearTimeout);
  }, []);

  // Auto-scroll to bottom when new messages appear
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTo({
        top: scrollRef.current.scrollHeight,
        behavior: "smooth"
      });
    }
  }, [visibleMessages]);

  return (
    <div className="relative w-full max-w-[340px] mx-auto">
      {/* Phone outer frame */}
      <div className="relative rounded-[2rem] border-[3px] border-white/[0.08] bg-[#0A0A0A] p-1.5 shadow-2xl shadow-black/60">
        {/* Phone inner screen */}
        <div className="rounded-[1.4rem] overflow-hidden bg-[#121212]" style={{ height: "520px" }}>
          {/* Phone notch / status bar */}
          <div className="relative flex items-center justify-between px-5 pt-3 pb-2">
            <span className="text-[10px] text-[#A3A3A3] font-semibold" style={{ fontFamily: "Manrope, sans-serif" }}>9:41</span>
            <div className="absolute left-1/2 -translate-x-1/2 top-2 w-20 h-5 bg-black rounded-full"></div>
            <div className="flex items-center gap-1">
              <Signal className="w-3 h-3 text-[#A3A3A3]" />
              <Wifi className="w-3 h-3 text-[#A3A3A3]" />
              <Battery className="w-3.5 h-3.5 text-[#A3A3A3]" />
            </div>
          </div>

          {/* App header */}
          <div className="flex items-center justify-between px-4 py-2.5 border-b border-white/[0.06]">
            <span className="text-xs text-white font-semibold" style={{ fontFamily: "Manrope, sans-serif" }}>Velocity AI</span>
            <div className="flex items-center gap-1.5">
              <div className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse"></div>
              <span className="text-[10px] text-emerald-400 font-medium" style={{ fontFamily: "Manrope, sans-serif" }}>Active</span>
            </div>
          </div>

          {/* Chat area — fixed height, scrollable */}
          <div
            ref={scrollRef}
            className="flex flex-col gap-3 p-4 overflow-y-auto"
            style={{ height: "calc(520px - 88px - 48px)" }}>

            <AnimatePresence mode="popLayout">
              {chatMessages.map((msg, i) => {
                if (!visibleMessages.includes(i)) return null;
                return (
                  <motion.div
                    key={i}
                    initial={{ opacity: 0, y: 16, scale: 0.97 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    transition={{ duration: 0.4, ease: [0.25, 0.46, 0.45, 0.94] }}
                    layout>

                    <ChatBubble msg={msg} i={i} />
                  </motion.div>);

              })}
            </AnimatePresence>
          </div>

          {/* Input bar */}
          <div className="absolute bottom-[6px] left-[6px] right-[6px] rounded-b-[1.4rem] bg-[#121212] border-t border-white/[0.06] px-4 py-3 flex items-center gap-3">
            <div className="flex-1 h-8 rounded-full bg-[#1A1A1A] border border-white/[0.06] px-3 flex items-center">
              <span className="text-[10px] text-[#A3A3A3]/50" style={{ fontFamily: "Manrope, sans-serif" }}>Message...</span>
            </div>
            <div className="w-8 h-8 rounded-full bg-[#5B8DEF]/20 flex items-center justify-center">
              <ArrowRight className="w-3.5 h-3.5 text-[#5B8DEF]" />
            </div>
          </div>
        </div>
      </div>

      {/* Glow effect */}
      <div className="absolute -inset-6 bg-[#5B8DEF]/[0.04] rounded-[3rem] blur-3xl -z-10"></div>
    </div>);

}

export default function HeroSection() {
  return (
    <section
      id="hero"
      data-testid="hero-section"
      className="relative min-h-screen flex items-center pt-16"
      style={{ background: "linear-gradient(180deg, #0A0A0A 0%, #0D0D0F 100%)" }}>

      <div
        className="absolute inset-0 opacity-[0.03]"
        style={{
          backgroundImage: "linear-gradient(rgba(255,255,255,.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,.1) 1px, transparent 1px)",
          backgroundSize: "60px 60px"
        }} />


      <div className="relative max-w-7xl mx-auto px-6 md:px-12 w-full py-24 md:py-32">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          {/* Left: Content */}
          <div>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}>

              <span
                className="inline-flex items-center gap-2 text-sm uppercase tracking-widest text-[#5B8DEF] font-medium mb-6"
                style={{ fontFamily: "Manrope, sans-serif" }}
                data-testid="hero-label">

                <span className="w-6 h-px bg-[#5B8DEF]"></span>
                AI-Powered Call Capture
              </span>
            </motion.div>

            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="text-4xl sm:text-5xl lg:text-6xl tracking-tighter font-semibold text-white leading-[1.08] mb-6"
              style={{ fontFamily: "Outfit, sans-serif" }}
              data-testid="hero-headline">

              Stop Missing Calls.
              <br />
              <span className="text-[#5B8DEF]">Start Booking Jobs.</span>
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
              className="text-lg text-[#A3A3A3] leading-relaxed mb-10 max-w-lg"
              style={{ fontFamily: "Manrope, sans-serif" }}
              data-testid="hero-subheadline">Our AI captures every missed call, instantly responds to leads, books jobs on your calendar, and follows up for 5-star reviews, so you never lose another customer.


            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
              className="flex flex-wrap gap-4 mb-8">

              <a
                href={TRIAL_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="hero-cta-trial"
                className="group inline-flex items-center gap-2.5 bg-[#5B8DEF] hover:bg-[#4A79D3] text-white font-semibold px-7 py-3.5 rounded-lg text-base transition-all duration-200 active:scale-[0.97]"
                style={{ fontFamily: "Manrope, sans-serif" }}>

                Start Free 3-Day Trial
                <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform duration-200" />
              </a>
              <a
                href={DEMO_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="hero-cta-demo"
                className="group inline-flex items-center gap-2.5 bg-transparent border border-white/15 hover:border-white/30 text-white font-semibold px-7 py-3.5 rounded-lg text-base transition-all duration-200 active:scale-[0.97]"
                style={{ fontFamily: "Manrope, sans-serif" }}>

                <Play className="w-4 h-4 text-[#A3A3A3] group-hover:text-white transition-colors duration-200" />
                Book a Demo
              </a>
            </motion.div>

            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.6, delay: 0.6 }}
              className="flex items-center gap-3 !opacity-[0%]"
              data-testid="hero-social-proof">

              <div className="flex -space-x-2">
                {[1, 2, 3, 4].map((i) =>
                <div
                  key={i}
                  className="w-7 h-7 rounded-full border-2 border-[#0A0A0A] bg-[#1A1A1A] flex items-center justify-center">

                    <span className="text-[9px] text-[#A3A3A3] font-medium" style={{ fontFamily: "Manrope, sans-serif" }}>
                      {["MH", "SC", "JR", "LM"][i - 1]}
                    </span>
                  </div>
                )}
              </div>
              <div className="flex items-center gap-1">
                {[1, 2, 3, 4, 5].map((i) =>
                <Star key={i} className="w-3.5 h-3.5 text-amber-400 fill-amber-400" />
                )}
              </div>
              <span className="text-sm text-[#A3A3A3]" style={{ fontFamily: "Manrope, sans-serif" }}>
                Trusted by 50+ plumbing businesses
              </span>
            </motion.div>
          </div>

          {/* Right: Phone Mockup — visible on ALL screens */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.5 }}
            data-testid="hero-phone-mockup">

            <PhoneMockup />
          </motion.div>
        </div>
      </div>

      <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-[#0A0A0A] to-transparent"></div>
    </section>);

}